import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowRight, BookOpen, Calendar } from "lucide-react";
import { format } from "date-fns";
import { pt } from "date-fns/locale";

export function LatestBlogPosts() {
  const { data: articles, isLoading } = useQuery({
    queryKey: ["latest-blog-posts-homepage"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("blog_articles")
        .select("id, title, slug, excerpt, featured_image, published_at, blog_categories(name, slug)")
        .eq("is_published", true)
        .order("published_at", { ascending: false })
        .limit(3);

      if (error) throw error;
      return data;
    },
    staleTime: 5 * 60 * 1000,
  });

  if (isLoading || !articles || articles.length === 0) {
    return null;
  }

  return (
    <section className="py-10 md:py-12 bg-background">
      <div className="container">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-8 gap-4">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary mb-3">
              <BookOpen className="h-4 w-4" />
              <span className="text-sm font-medium">Blog & Notícias</span>
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-foreground">
              Últimos Artigos
            </h2>
            <p className="text-muted-foreground mt-2 max-w-lg">
              Dicas, tutoriais e novidades sobre impressão digital DTF, UV e serigrafia
            </p>
          </div>
          <Button variant="outline" asChild className="w-fit">
            <Link to="/blog">
              Ver Todos os Artigos
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {articles.map((article) => (
            <Card
              key={article.id}
              className="overflow-hidden border-border/50 group hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
            >
              <Link to={`/blog/${article.slug}`} className="block">
                <div className="aspect-video relative overflow-hidden bg-muted">
                  {article.featured_image ? (
                    <img
                      src={article.featured_image}
                      alt={article.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                      decoding="async"
                      width={400}
                      height={225}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <BookOpen className="h-12 w-12 text-muted-foreground/30" />
                    </div>
                  )}
                </div>
                <CardContent className="p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <Badge variant="secondary" className="text-xs">
                      {(article.blog_categories as any)?.name || "Blog"}
                    </Badge>
                    <span className="text-xs text-muted-foreground flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      {article.published_at &&
                        format(new Date(article.published_at), "d MMM yyyy", { locale: pt })}
                    </span>
                  </div>
                  <h3 className="font-semibold text-foreground mb-2 line-clamp-2 group-hover:text-primary transition-colors">
                    {article.title}
                  </h3>
                  {article.excerpt && (
                    <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
                      {article.excerpt}
                    </p>
                  )}
                  <span className="text-sm text-primary font-medium inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                    Ler artigo
                    <ArrowRight className="h-3 w-3" />
                  </span>
                </CardContent>
              </Link>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
}
